import type { Except } from "type-fest";
import { z } from "zod";
import type {
    IsoAndUnixTimestamp} from "../date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "../date-time/iso-and-unix";
import {
    type MetaContent,
    metaContentZodSchema,
} from "../meta-content/meta-content";
import type {
    ProviderGroupId} from "./provider-group-id";
import {
    providerGroupIdFactory,
    providerGroupIdZodSchema,
} from "./provider-group-id";

type ProviderGroup = {
    readonly _id: ProviderGroupId;
    readonly _type: "PROVIDER_GROUP";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly meta: MetaContent;
    /**
     * friendly name of provider group. \
     * at least 5 chars long
     * @type {string}
     */
    readonly slug: string;
    /**
     * friendly name of provider group. \
     * at least 3 chars long
     * @type {string}
     */
    readonly area: string;
    /**
     * default is https://<slug>.towni.se \
     * but could be e.g. https://camping.towni.se \
     * or just simply https://towni.se \
     * @type {string}
     */
    readonly endpoint: string;
};

type FactoryParams = Except<ProviderGroup, "_createdAt" | "_id" | "_type">;
const providerGroupFactory = (params: FactoryParams): ProviderGroup => {
    return {
        ...params,
        _id: providerGroupIdFactory(),
        _createdAt: nowIsoAndUnix(),
        _type: "PROVIDER_GROUP",
    };
};

const providerGroupZodSchema: z.ZodSchema<ProviderGroup> = z.object({
    _createdAt: isoAndUnixZodSchema,
    _id: providerGroupIdZodSchema,
    _type: z.literal("PROVIDER_GROUP"),
    endpoint: z.string().url(),
    meta: metaContentZodSchema,
    slug: z.string().min(5),
    area: z.string().min(3),
});

export { providerGroupFactory, providerGroupZodSchema };
export type { ProviderGroup };
